import apiTamboUrl from "@/services/api";
import { getErrorMessage, isSuccessStatus } from "@/services/utils/httpUtils";

/**
 * Store para gestionar participantes del tambo
 * - Invitar usuarios
 * - Asignar roles
 * - Listar participantes
 * - Eliminar participantes
 */
export default {
  namespaced: true,

  state: {
    participantes: [],
    invitaciones: [],
    rolesInfo: [], // Información de roles disponibles del backend
    loading: false,
    error: null,
  },

  getters: {
    participantes(state) {
      return state.participantes;
    },

    // Participantes activos (con rol asignado)
    participantesActivos(state) {
      return state.participantes.filter((p) => p.role !== "pendiente");
    },

    // Participantes pendientes (sin rol asignado)
    participantesPendientes(state) {
      return state.participantes.filter((p) => p.role === "pendiente");
    },

    // Obtener participante por ID
    getById: (state) => (id) => {
      return state.participantes.find((p) => p.id === id);
    },

    // Obtener participante por user_id
    getByUserId: (state) => (userId) => {
      return state.participantes.find((p) => p.user_id === userId);
    },

    rolesInfo(state) {
      return state.rolesInfo;
    },

    invitaciones(state) {
      return state.invitaciones;
    },

    loading(state) {
      return state.loading;
    },

    error(state) {
      return state.error;
    },
  },

  mutations: {
    setParticipantes(state, participantes) {
      state.participantes = participantes;
    },

    addParticipante(state, participante) {
      state.participantes.push(participante);
    },

    updateParticipante(state, updatedParticipante) {
      const index = state.participantes.findIndex(
        (p) => p.id === updatedParticipante.id
      );
      if (index !== -1) {
        state.participantes.splice(index, 1, updatedParticipante);
      }
    },

    removeParticipante(state, userId) {
      state.participantes = state.participantes.filter(
        (p) => p.user_id !== userId
      );
    },

    setRolesInfo(state, rolesInfo) {
      state.rolesInfo = rolesInfo;
    },

    setInvitaciones(state, invitaciones) {
      state.invitaciones = invitaciones;
    },

    removeInvitacion(state, invitacionId) {
      state.invitaciones = state.invitaciones.filter(
        (i) => i.id !== invitacionId
      );
    },

    setLoading(state, value) {
      state.loading = value;
    },

    setError(state, error) {
      state.error = error;
    },

    clearError(state) {
      state.error = null;
    },

    clearState(state) {
      state.participantes = [];
      state.invitaciones = [];
      state.error = null;
    },
  },

  actions: {
    /**
     * Obtener lista de participantes del tambo actual
     */
    async getParticipantes({ commit, rootState }) {
      const tamboId = rootState.userTamboStore.currentTamboId;
      if (!tamboId) {
        commit("setError", "No hay tambo seleccionado");
        return;
      }

      commit("setLoading", true);
      commit("clearError");

      try {
        const response = await apiTamboUrl.get(
          `/tambos/${tamboId}/participantes/`
        );

        if (isSuccessStatus(response.status)) {
          commit("setParticipantes", response.data);
          console.log("📋 Participantes obtenidos:", response.data);
          return response.data;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error al obtener participantes"
        );
        commit("setError", errorMessage);
        console.error("❌ Error obteniendo participantes:", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Invitar un usuario al tambo
     * @param {string} username - Nombre de usuario a invitar
     */
    async invitarUsuario({ commit, rootState }, username) {
      const tamboId = rootState.userTamboStore.currentTamboId;
      if (!tamboId) {
        commit("setError", "No hay tambo seleccionado");
        return null;
      }

      commit("setLoading", true);
      commit("clearError");

      try {
        const response = await apiTamboUrl.post(
          `/tambos/${tamboId}/agregar-participante/`,
          { username }
        );

        if (isSuccessStatus(response.status)) {
          commit("addParticipante", response.data);
          console.log("✅ Usuario invitado:", response.data);
          return response.data;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error, "Error al invitar usuario");
        commit("setError", errorMessage);
        console.error("❌ Error invitando usuario:", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Invitar usuario por email (envía correo automáticamente)
     * POST /tambos/{tambo_id}/invitar/email
     * @param {object} data - { email, role }
     */
    async invitarPorEmail({ commit, rootState }, { email, role }) {
      const tamboId = rootState.userTamboStore.currentTamboId;
      if (!tamboId) {
        commit("setError", "No hay tambo seleccionado");
        return null;
      }

      commit("setLoading", true);
      commit("clearError");

      try {
        const response = await apiTamboUrl.post(
          `/tambos/${tamboId}/invitar/email`,
          { email, role }
        );

        if (isSuccessStatus(response.status)) {
          console.log("✅ Invitación por email creada:", response.data);
          return response.data;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error al enviar invitación por email"
        );
        commit("setError", errorMessage);
        console.error("❌ Error invitación email:", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Invitar usuario por WhatsApp (genera link de invitación sin email)
     * POST /tambos/{tambo_id}/invitar/whatsapp
     * @param {object} data - { role }
     */
    async invitarPorWhatsApp({ commit, rootState }, { role }) {
      const tamboId = rootState.userTamboStore.currentTamboId;
      if (!tamboId) {
        commit("setError", "No hay tambo seleccionado");
        return null;
      }

      commit("setLoading", true);
      commit("clearError");

      try {
        const response = await apiTamboUrl.post(
          `/tambos/${tamboId}/invitar/whatsapp`,
          { role }
        );

        if (isSuccessStatus(response.status)) {
          console.log("✅ Invitación WhatsApp creada:", response.data);
          return response.data;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error al generar invitación WhatsApp"
        );
        commit("setError", errorMessage);
        console.error("❌ Error invitación WhatsApp:", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Obtener lista de invitaciones pendientes del tambo
     */
    async getInvitaciones({ commit, rootState }) {
      const tamboId = rootState.userTamboStore.currentTamboId;
      if (!tamboId) return;

      try {
        const response = await apiTamboUrl.get(
          `/tambos/${tamboId}/invitaciones/`
        );

        if (isSuccessStatus(response.status)) {
          commit("setInvitaciones", response.data);
          console.log("📋 Invitaciones obtenidas:", response.data);
          return response.data;
        }
      } catch (error) {
        console.error("⚠️ Error obteniendo invitaciones:", error);
        return [];
      }
    },

    /**
     * Cancelar una invitación pendiente
     * @param {number} invitacionId - ID de la invitación a cancelar
     */
    async cancelarInvitacion({ commit, rootState }, invitacionId) {
      const tamboId = rootState.userTamboStore.currentTamboId;
      if (!tamboId) return;

      commit("setLoading", true);
      commit("clearError");

      try {
        const response = await apiTamboUrl.delete(
          `/tambos/${tamboId}/invitaciones/${invitacionId}/`
        );

        if (isSuccessStatus(response.status)) {
          commit("removeInvitacion", invitacionId);
          console.log("✅ Invitación cancelada:", invitacionId);
          return true;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error al cancelar invitación"
        );
        commit("setError", errorMessage);
        console.error("❌ Error cancelando invitación:", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Asignar rol a un participante
     * @param {number} userId - ID del usuario
     * @param {string} role - Rol a asignar
     * @param {object} permisos - Permisos personalizados (opcional)
     */
    async asignarRol({ commit, rootState }, { userId, role, permisos = null }) {
      const tamboId = rootState.userTamboStore.currentTamboId;
      if (!tamboId) {
        commit("setError", "No hay tambo seleccionado");
        return null;
      }

      commit("setLoading", true);
      commit("clearError");

      try {
        const payload = { role };
        if (permisos) {
          payload.permisos = permisos;
        }

        const response = await apiTamboUrl.patch(
          `/tambos/${tamboId}/participantes/${userId}/rol/`,
          payload
        );

        if (isSuccessStatus(response.status)) {
          commit("updateParticipante", response.data);
          console.log("✅ Rol asignado:", response.data);
          return response.data;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error, "Error al asignar rol");
        commit("setError", errorMessage);
        console.error("❌ Error asignando rol:", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Habilitar o revocar permisos de ingreso al sistema para un empleado
     */
    async cambiarEstadoAcceso({ commit, rootState }, { userId, activo }) {
      const tamboId = rootState.userTamboStore.currentTamboId;
      if (!tamboId) {
        commit("setError", "No hay tambo seleccionado");
        return null;
      }

      commit("setLoading", true);
      commit("clearError");

      try {
        const response = await apiTamboUrl.patch(
          `/tambos/${tamboId}/participantes/${userId}/estado-acceso/`,
          { activo }
        );

        if (isSuccessStatus(response.status)) {
          commit("updateParticipante", response.data);
          console.log(
            `✅ Estado de acceso actualizado (activo: ${activo}):`,
            response.data
          );
          return response.data;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error al cambiar estado de acceso"
        );
        commit("setError", errorMessage);
        console.error("❌ Error cambiando estado de acceso:", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Configurar vistas personalizadas a las que tiene acceso el empleado
     */
    async configurarVistas({ commit, rootState }, { userId, vistas }) {
      const tamboId = rootState.userTamboStore.currentTamboId;
      if (!tamboId) {
        commit("setError", "No hay tambo seleccionado");
        return null;
      }

      commit("setLoading", true);
      commit("clearError");

      try {
        const response = await apiTamboUrl.patch(
          `/tambos/${tamboId}/participantes/${userId}/vistas/`,
          { vistas }
        );

        if (isSuccessStatus(response.status)) {
          commit("updateParticipante", response.data);
          console.log("✅ Vistas configuradas:", response.data);
          return response.data;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error al configurar vistas"
        );
        commit("setError", errorMessage);
        console.error("❌ Error configurando vistas:", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Restablecer vistas y permisos a los valores por defecto del rol
     */
    async resetearVistas({ commit, rootState }, userId) {
      const tamboId = rootState.userTamboStore.currentTamboId;
      if (!tamboId) {
        commit("setError", "No hay tambo seleccionado");
        return null;
      }

      commit("setLoading", true);
      commit("clearError");

      try {
        const response = await apiTamboUrl.post(
          `/tambos/${tamboId}/participantes/${userId}/vistas/reset/`
        );

        if (isSuccessStatus(response.status)) {
          commit("updateParticipante", response.data);
          console.log("✅ Vistas restablecidas a por defecto:", response.data);
          return response.data;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error al restablecer vistas"
        );
        commit("setError", errorMessage);
        console.error("❌ Error restableciendo vistas:", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Eliminar participante del tambo
     * @param {number} userId - ID del usuario a eliminar
     */
    async eliminarParticipante({ commit, rootState }, userId) {
      const tamboId = rootState.userTamboStore.currentTamboId;
      if (!tamboId) {
        commit("setError", "No hay tambo seleccionado");
        return false;
      }

      commit("setLoading", true);
      commit("clearError");

      try {
        const response = await apiTamboUrl.delete(
          `/tambos/${tamboId}/participantes/${userId}/`
        );

        if (isSuccessStatus(response.status)) {
          commit("removeParticipante", userId);
          console.log("✅ Participante eliminado");
          return true;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error al eliminar participante"
        );
        commit("setError", errorMessage);
        console.error("❌ Error eliminando participante:", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Obtener información de roles disponibles (del backend)
     */
    async getRolesInfo({ commit }) {
      try {
        const response = await apiTamboUrl.get("/roles-info/");

        if (isSuccessStatus(response.status)) {
          commit("setRolesInfo", response.data);
          console.log("📋 Roles info obtenidos:", response.data);
          return response.data;
        }
      } catch (error) {
        console.error("⚠️ Error obteniendo roles info:", error);
        // No lanzar error, usaremos los roles locales como fallback
        return null;
      }
    },

    /**
     * Limpiar estado (al cambiar de tambo)
     */
    clearParticipantes({ commit }) {
      commit("clearState");
    },
  },
};
